import { transactionFlow } from './report';
import type { FinanceCategoryKind } from './types';

export type ActivityAccount = {
  id: number;
  name: string;
  currencyCode: string;
  isActive: boolean;
  sortOrder: number;
};

export type ActivityTransaction = {
  date: string;
  accountId: number | null;
  toAccountId: number | null;
  categoryId: number | null;
  currencyCode: string;
  amount: number;
  amountUsd: number | null;
};

export type ActivityCell = {
  count: number;
  /** Net change in the account currency. Null when nothing in that currency posted. */
  net: number | null;
  foreignCount: number;
  /** Net change in USD. Null when no row in the cell had an FX rate. */
  netUsd: number | null;
};

export type ActivityRow = {
  /** Null is the bucket for transactions with no account. */
  accountId: number | null;
  label: string;
  currencyCode: string | null;
  isActive: boolean;
  cells: ActivityCell[];
  totalCount: number;
  totalNet: number | null;
  totalUsd: number | null;
};

export type AccountActivity = {
  months: string[];
  rows: ActivityRow[];
  /** Distinct transactions in each month. A transfer is one transaction. */
  countsByMonth: number[];
  netUsdByMonth: Array<number | null>;
  totalCount: number;
  totalUsd: number | null;
};

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function emptyCell(): ActivityCell {
  return { count: 0, net: null, foreignCount: 0, netUsd: null };
}

function add(current: number | null, delta: number): number {
  return round2((current ?? 0) + delta);
}

/**
 * Accounts × months. Each cell counts transactions that touch the account
 * and the net cash movement. A transfer is counted on both accounts.
 * Active accounts stay in the matrix even when a month is empty.
 */
export function buildAccountActivity(
  accounts: ActivityAccount[],
  categories: Array<{ id: number; kind: FinanceCategoryKind }>,
  transactions: ActivityTransaction[],
  months: string[],
): AccountActivity {
  const monthIndex = new Map(months.map((month, index) => [month, index]));
  const kindById = new Map(categories.map((category) => [category.id, category.kind]));
  const accountsById = new Map(accounts.map((account) => [account.id, account]));
  const cells = new Map<number | null, ActivityCell[]>();
  const ensure = (accountId: number | null): ActivityCell[] => {
    let row = cells.get(accountId);
    if (!row) {
      row = months.map(() => emptyCell());
      cells.set(accountId, row);
    }
    return row;
  };
  const seen = months.map(() => new Set<number>());

  transactions.forEach((tx, txIndex) => {
    const month = tx.date.slice(0, 7);
    const index = monthIndex.get(month);
    if (index == null) return;
    const flow = transactionFlow(kindById, tx);
    const magnitude = Math.abs(tx.amount);
    const usdMagnitude = tx.amountUsd == null ? null : Math.abs(tx.amountUsd);
    seen[index]!.add(txIndex);

    const hit = (accountId: number | null, inbound: boolean) => {
      const row = ensure(accountId);
      const cell = row[index]!;
      cell.count += 1;
      const sign = inbound ? 1 : -1;
      if (usdMagnitude != null) cell.netUsd = add(cell.netUsd, sign * usdMagnitude);
      if (accountId == null) {
        cell.net = add(cell.net, sign * magnitude);
        return;
      }
      const account = accountsById.get(accountId);
      if (!account || tx.currencyCode !== account.currencyCode) {
        cell.foreignCount += 1;
        return;
      }
      cell.net = add(cell.net, sign * magnitude);
    };

    if (flow === 'transfer') {
      const inbound = tx.amount < 0;
      if (tx.accountId != null) hit(tx.accountId, inbound);
      if (tx.toAccountId != null && tx.toAccountId !== tx.accountId) hit(tx.toAccountId, !inbound);
      if (tx.accountId == null && (tx.toAccountId == null || tx.toAccountId === tx.accountId)) {
        hit(null, inbound);
      }
      return;
    }
    hit(tx.accountId, flow === 'income');
  });

  const summarize = (
    accountId: number | null,
    label: string,
    currencyCode: string | null,
    isActive: boolean,
    rowCells: ActivityCell[],
  ): ActivityRow => {
    let totalCount = 0;
    let totalNet: number | null = null;
    let totalUsd: number | null = null;
    for (const cell of rowCells) {
      totalCount += cell.count;
      if (cell.net != null) totalNet = add(totalNet, cell.net);
      if (cell.netUsd != null) totalUsd = add(totalUsd, cell.netUsd);
    }
    return {
      accountId,
      label,
      currencyCode,
      isActive,
      cells: rowCells,
      totalCount,
      totalNet,
      totalUsd,
    };
  };

  const byName = (a: ActivityAccount, b: ActivityAccount) =>
    a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  const active = accounts.filter((account) => account.isActive).sort(byName);
  const inactive = accounts
    .filter(
      (account) =>
        !account.isActive && (cells.get(account.id)?.some((cell) => cell.count > 0) ?? false),
    )
    .sort(byName);

  const rows: ActivityRow[] = [];
  for (const account of [...active, ...inactive]) {
    rows.push(
      summarize(
        account.id,
        account.name,
        account.currencyCode,
        account.isActive,
        cells.get(account.id) ?? months.map(() => emptyCell()),
      ),
    );
  }
  const unassigned = cells.get(null);
  if (unassigned?.some((cell) => cell.count > 0)) {
    rows.push(summarize(null, 'Unassigned', null, true, unassigned));
  }

  const countsByMonth = seen.map((ids) => ids.size);
  const netUsdByMonth = months.map((_, index) => {
    let total: number | null = null;
    for (const row of rows) {
      const net = row.cells[index]?.netUsd;
      if (net != null) total = add(total, net);
    }
    return total;
  });
  const totalCount = countsByMonth.reduce((sum, count) => sum + count, 0);
  let totalUsd: number | null = null;
  for (const net of netUsdByMonth) {
    if (net != null) totalUsd = add(totalUsd, net);
  }

  return { months, rows, countsByMonth, netUsdByMonth, totalCount, totalUsd };
}
